<script setup lang="ts">
import { useDeckPlayerContext } from '../../composables/useDeckPlayerShell'
import { vimeoEmbedUrl, youtubeEmbedUrl, type ResolvedMedia } from '../../flowchart/runtime/media'
import NowPlayingControls from './NowPlayingControls.vue'

/** Media shown in place of the card (standalone mode). */
defineProps<{ resolvedMedia: ResolvedMedia }>()

const { nowPlaying } = useDeckPlayerContext()
const {
  initYoutube: initialiseYoutubePlayer,
  initVimeo: initialiseVimeoPlayer,
  logNativeEvent: logNativeMediaEvent,
  onNativeEnded: handleNativeMediaEnded,
  onNativePlay: handleNowPlayingMediaPlay,
  onNativePause: handleNowPlayingMediaPause,
  bindMediaEl,
  bindYoutubeFrame,
  bindVimeoFrame,
} = nowPlaying
</script>

<template>
  <div class="pfc-media-inline">
    <div class="pfc-media-inline__content">
      <img
        v-if="resolvedMedia.mediaType === 'image'"
        :src="resolvedMedia.primaryUrl || undefined"
        class="pfc-media-img"
        :alt="resolvedMedia.mediaName"
      />
      <div v-else-if="resolvedMedia.mediaType === 'video'" class="pfc-media-video-wrap">
        <iframe
          v-if="youtubeEmbedUrl(resolvedMedia.primaryUrl, !resolvedMedia.skippable)"
          :ref="bindYoutubeFrame"
          :src="youtubeEmbedUrl(resolvedMedia.primaryUrl, !resolvedMedia.skippable)!"
          class="pfc-media-video"
          allow="
            accelerometer;
            autoplay;
            clipboard-write;
            encrypted-media;
            gyroscope;
            picture-in-picture;
          "
          allowfullscreen
          @load="initialiseYoutubePlayer"
        />
        <iframe
          v-else-if="vimeoEmbedUrl(resolvedMedia.primaryUrl, !resolvedMedia.skippable)"
          :ref="bindVimeoFrame"
          :src="vimeoEmbedUrl(resolvedMedia.primaryUrl, !resolvedMedia.skippable)!"
          class="pfc-media-video"
          allow="
            accelerometer;
            autoplay;
            clipboard-write;
            encrypted-media;
            gyroscope;
            picture-in-picture;
          "
          allowfullscreen
          @load="initialiseVimeoPlayer"
        />
        <video
          v-else-if="resolvedMedia.primaryUrl"
          :ref="bindMediaEl"
          :src="resolvedMedia.primaryUrl"
          :controls="resolvedMedia.skippable"
          :autoplay="!resolvedMedia.skippable"
          playsinline
          class="pfc-media-video"
          @play="handleNowPlayingMediaPlay"
          @pause="handleNowPlayingMediaPause"
          @seeking="logNativeMediaEvent"
          @seeked="logNativeMediaEvent"
          @ratechange="logNativeMediaEvent"
          @ended="handleNativeMediaEnded"
        />
        <div v-else class="pfc-media-placeholder">{{ resolvedMedia.mediaName }}</div>
        <NowPlayingControls
          v-if="!resolvedMedia.skippable && resolvedMedia.primaryUrl"
          kind="video"
        />
      </div>
      <div v-else-if="resolvedMedia.mediaType === 'audio'" class="pfc-media-audio-wrap">
        <NowPlayingControls v-if="!resolvedMedia.skippable" kind="audio" />
        <audio
          v-if="resolvedMedia.primaryUrl"
          :ref="bindMediaEl"
          :src="resolvedMedia.primaryUrl"
          :controls="resolvedMedia.skippable"
          :autoplay="!resolvedMedia.skippable"
          :class="['pfc-media-audio', { 'pfc-media-audio--hidden': !resolvedMedia.skippable }]"
          @play="handleNowPlayingMediaPlay"
          @pause="handleNowPlayingMediaPause"
          @seeking="logNativeMediaEvent"
          @seeked="logNativeMediaEvent"
          @ratechange="logNativeMediaEvent"
          @ended="handleNativeMediaEnded"
        />
        <div v-else class="pfc-media-placeholder">{{ resolvedMedia.mediaName }}</div>
      </div>
      <iframe
        v-else-if="resolvedMedia.mediaType === 'pdf'"
        :src="resolvedMedia.primaryUrl || undefined"
        class="pfc-media-pdf"
      />
      <div v-else class="pfc-media-placeholder">{{ resolvedMedia.mediaName }}</div>
    </div>
  </div>
</template>

<style scoped src="./styles/playerMedia.css"></style>
